<template>
  <div class="filters-wrapper">
    <div class="filter">
      <DatePicker
        ref="datepicker"
        v-model="tempDateRange"
        range
        format="DD/MM/YYYY"
        :enable-time-picker="false"
        :partial-range="true"
        :auto-apply="false"
        :min-date="minSelectableDate"
        :max-date="maxSelectableDate"
        :disabled-date="isDateDisabled"
        :multi-calendars="false"
      >
        <template #input>
          <button class="filter-button" @click="openCalendar">
            {{ formattedDateRange }}
          </button>
        </template>
      </DatePicker>
    </div>

    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('source')">
          {{
            selectedSources.length
              ? $t('C2W_CAMPAIGNS_REPORT.COMMON.SELECTED_COUNT', {
                  count: selectedSources.length,
                })
              : $t('C2W_CAMPAIGNS_REPORT.FILTERS.SOURCE')
          }}
        </button>
        <div v-if="openDropdown === 'source'" class="dropdown-content">
          <input
            type="text"
            v-model="sourceSearch"
            class="search-input"
            :placeholder="$t('C2W_CAMPAIGNS_REPORT.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="s in filteredSources" :key="s" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="s"
                v-model="selectedSources"
                @change="emitFilters"
              />
              {{ s }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('source_url')">
          {{
            selectedLinks.length
              ? $t('C2W_CAMPAIGNS_REPORT.COMMON.SELECTED_COUNT', {
                  count: selectedLinks.length,
                })
              : $t('C2W_CAMPAIGNS_REPORT.FILTERS.SOURCE_URL')
          }}
        </button>
        <div v-if="openDropdown === 'source_url'" class="dropdown-content">
          <input
            type="text"
            v-model="linkSearch"
            class="search-input"
            :placeholder="$t('C2W_CAMPAIGNS_REPORT.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="l in filteredLinks" :key="l" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="l"
                v-model="selectedLinks"
                @change="emitFilters"
              />
              {{ l }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('headline')">
          {{
            selectedHeadlines.length
              ? $t('C2W_CAMPAIGNS_REPORT.COMMON.SELECTED_COUNT', {
                  count: selectedHeadlines.length,
                })
              : $t('C2W_CAMPAIGNS_REPORT.FILTERS.HEADLINE')
          }}
        </button>
        <div v-if="openDropdown === 'headline'" class="dropdown-content">
          <input
            type="text"
            v-model="headlineSearch"
            class="search-input"
            :placeholder="$t('C2W_CAMPAIGNS_REPORT.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="h in filteredHeadlines" :key="h" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="h"
                v-model="selectedHeadlines"
                @change="emitFilters"
              />
              {{ h }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="filter">
      <div class="custom-dropdown">
        <button class="filter-button" @click="toggleDropdown('inbox')">
          {{
            selectedInboxes.length
              ? $t('C2W_CAMPAIGNS_REPORT.COMMON.SELECTED_COUNT', {
                  count: selectedInboxes.length,
                })
              : 'Inbox'
          }}
        </button>
        <div v-if="openDropdown === 'inbox'" class="dropdown-content">
          <input
            type="text"
            v-model="inboxSearch"
            class="search-input"
            :placeholder="$t('C2W_CAMPAIGNS_REPORT.FILTERS.TYPE_TO_SEARCH')"
          />
          <div v-for="i in filteredInboxes" :key="i" class="checkbox-option">
            <label>
              <input
                type="checkbox"
                :value="i"
                v-model="selectedInboxes"
                @change="emitFilters"
              />
              {{ i }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'CampaignsFilters',
  components: { DatePicker },
  props: {
    campaignsData: { type: Array, required: true },
  },
  data() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return {
      tempDateRange: [sevenDaysAgo, today],
      customDateRange: [sevenDaysAgo, today],
      openDropdown: null,
      selectedSources: [],
      selectedLinks: [],
      selectedHeadlines: [],
      selectedInboxes: [],
      sourceSearch: '',
      linkSearch: '',
      headlineSearch: '',
      inboxSearch: '',
    };
  },
  computed: {
    formattedDateRange() {
      const [start, end] = this.tempDateRange;
      if (!start || !end) {
        return this.$t('C2W_CAMPAIGNS_REPORT.FILTERS.SELECT_DATE_RANGE');
      }

      const format = d =>
        new Date(d).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });

      return `${format(start)} ~ ${format(end)}`;
    },
    filteredSources() {
      const sources = [
        ...new Set(this.campaignsData.map(i => i.source).filter(Boolean)),
      ];
      return sources.filter(s =>
        s.toLowerCase().includes(this.sourceSearch.toLowerCase())
      );
    },
    filteredLinks() {
      const links = [
        ...new Set(this.campaignsData.map(i => i.source_url).filter(Boolean)),
      ];
      return links.filter(l =>
        l.toLowerCase().includes(this.linkSearch.toLowerCase())
      );
    },
    filteredHeadlines() {
      const headlines = [
        ...new Set(this.campaignsData.map(i => i.headline).filter(Boolean)),
      ];
      return headlines.filter(h =>
        h.toLowerCase().includes(this.headlineSearch.toLowerCase())
      );
    },
    filteredInboxes() {
      const inboxes = [
        ...new Set(this.campaignsData.map(i => i.inbox_name).filter(Boolean)),
      ];
      return inboxes.filter(i =>
        i.toLowerCase().includes(this.inboxSearch.toLowerCase())
      );
    },
    today() {
      return new Date();
    },
    minSelectableDate() {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(this.today.getFullYear() - 1);
      return oneYearAgo;
    },
    maxSelectableDate() {
      return this.today;
    },
  },
  watch: {
    tempDateRange(newRange) {
      if (newRange[0] && newRange[1]) {
        this.customDateRange = newRange;
        this.resetFiltersExceptDate();
        this.emitFilters();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    emitFilters() {
      this.$emit('filter-change', {
        customDateRange: this.customDateRange,
        sources: this.selectedSources,
        sourceUrls: this.selectedLinks,
        headlines: this.selectedHeadlines,
        inboxes: this.selectedInboxes,
      });
    },
    resetFiltersExceptDate() {
      this.selectedSources = [];
      this.selectedLinks = [];
      this.selectedHeadlines = [];
      this.selectedInboxes = [];
      this.sourceSearch = '';
      this.linkSearch = '';
      this.headlineSearch = '';
      this.inboxSearch = '';
    },
    toggleDropdown(name) {
      this.openDropdown = this.openDropdown === name ? null : name;
    },
    openCalendar() {
      this.$refs.datepicker.openMenu?.();
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.openDropdown = null;
      }
    },
    isDateDisabled(date) {
      return date < this.minSelectableDate || date > this.maxSelectableDate;
    },
  },
};
</script>

<style>
.filters-wrapper {
  width: 280px;
  min-height: 150vh;
  background-color: #000;
  padding: 20px;
  box-sizing: border-box;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-button {
  width: 240px;
  height: 50px;
  background: black !important;
  color: white !important;
  border: 1px solid white !important;
  padding: 10px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  appearance: none;
}

.filter-button:hover {
  background: #1a1a1a;
}

.custom-dropdown {
  position: relative;
  width: 240px;
}

.dropdown-content {
  position: absolute;
  top: 56px;
  background: #000;
  border: 1px solid white;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  z-index: 999;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.search-input {
  width: 100%;
  padding: 6px;
  margin-bottom: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.checkbox-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  gap: 8px;
}

.checkbox-option label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  color: white;
}

.checkbox-option input[type='checkbox'] {
  margin: 0;
}

::v-deep(.dp__input_icon),
.mx-icon-calendar,
.mx-icon-clear {
  display: none !important;
}
</style>
